import React from 'react'
import Popover from '@mui/material/Popover';
import { Button, Box, Typography } from '@mui/material';
import ModuleCancelButton from './ModuleCancelButton';
import ModuleScheduleUpdateButton from './ModuleScheduleUpdateButton';
import SetMakeUpEventButton from './SetMakeUpEventButton';
import ModuleZoomAssignButton from './ModuleZoomAssignButton';
import ModuleRecordedButton from './ModuleRecordedButton';
import ModuleTotalNumButton from './ModuleTotalNumButton';
import ModuleTotalHoursButton from './ModuleTotalHoursButton';
import ModuleCurrentHoursAdjustmentButton from './ModuleCurrentHoursAdjustmentButton';
import FindSubstituteButton from './FindSubstituteButton';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import Checkbox from '@mui/material/Checkbox';
import { DateTime } from 'luxon';
// import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
// import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

const ModuleButtonMenu = ({
    userId,
    zoom_id,
    date,
    _time,
    event_idx,
    event,
    enqueueSnackbar,
    colors,
    socketConnected,
    socket,
    zoomRooms,
    isThisAdminInViewMode,
    showModuleCheckBox,
    checkedModuleList,
    setCheckedModuleList
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);

    const LightTooltip = styled(({ className, ...props }) => (
            <Tooltip {...props} classes={{ popper: className }} />
        )
    )(
        ({ theme }) => ({
            [`& .${tooltipClasses.tooltip}`]: {
                backgroundColor: colors.grey[900],
                color: 'rgba(0, 0, 0, 0.87)',
                boxShadow: theme.shadows[5],
                fontSize: 14,
                border: `1px solid ${colors.grey[700]}`,
            },
        })
    );

    const handleButtonClick = (e) => {
        setAnchorEl(e.currentTarget);

        // need to emit to server to let other users know that this user is viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: true,
        });
    };

    const handlePopOverClose = () => {
        setAnchorEl(null);

        // need to emit to server to let other users know that this user is no longer viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: false,
        });
    };

    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover' : undefined;

    function getFontSize(start_end_diff) {
        if (!start_end_diff || start_end_diff === "1.00") {
            return "8px";
        } else {
            return "12px";
        }
    }

    function getEventTitle() {
        const title = event.title;

        if (!title) {
            return "n/a";
        }

        // Remove "Class " prefix to show "Module N"
        if (title.includes("Class ")) {
            return title.replace("Class ", "");
        }

        return title;
    }

    function getTotalSessionNum() {
        if (event.course && event.course.private_total_classes) {
            return event.course.private_total_classes;
        }
        if (event.total_event_num) {
            return event.total_event_num;
        }

        return "n/a";
    }

    function getCurrentEventNum() {
        if (event.curr_event_num !== undefined && event.curr_event_num !== null && event.curr_event_num >= 0) {
            return event.curr_event_num;
        }

        return "n/a";
    }

    function formatMinutesToHoursAndMinutes(totalMinutes) {
        if (totalMinutes === undefined || totalMinutes === null || totalMinutes < 0) {
            return "n/a";
        }
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours === 0 && minutes === 0) {
            return "0h";
        }
        if (hours === 0) {
            return `${minutes}m`;
        }
        if (minutes === 0) {
            return `${hours}h`;
        }
        return `${hours}h ${minutes}m`;
    }

    function getCurrentHours() {
        // Apply adjustment from course if available
        const adjustment = event.course?.current_minutes_adjustment || 0;
        const adjustedMinutes = (event.current_minutes || 0) + adjustment;
        return formatMinutesToHoursAndMinutes(adjustedMinutes);
    }

    function getTotalHours() {
        // Check course.total_minutes first (updated via socket)
        if (event.course && event.course.total_minutes >= 1) {
            return formatMinutesToHoursAndMinutes(event.course.total_minutes);
        }
        // Fall back to calculated total_minutes from weekly endpoint
        return formatMinutesToHoursAndMinutes(event.total_minutes);
    }

    const NewYork_Now = DateTime.local().setZone('America/New_York');
    const eventStartTime = event.start ? DateTime.fromISO(event.start).setZone('America/New_York') : null;
    const eventEndTime = event.end ? DateTime.fromISO(event.end).setZone('America/New_York') : null;

    {/* set deadline to be 2am of the next day after the event start time, if event start time is not null */}
    let deadline = eventStartTime ? eventStartTime.set({ hour: 23, minute: 0, second: 0 }) : null;
    deadline = deadline ? deadline.plus({ hours: 3 }) : null;

    return (
        <>
        <Box
            sx={{
                position: "absolute",
                top: 0,
                right: 0,
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1,
            }}
        >
            {/* if event.homework_exists is false and event end is before NewYork_Now and NewYork_Now is after 10 pm in new york time */}
            {!event.is_canceled && !event.homework_exists && deadline && NewYork_Now.toJSDate() > deadline.toJSDate() && eventEndTime.toJSDate() < deadline.toJSDate() && (
                <Tooltip title="Homework does not exist" placement="top" arrow>
                    {/* <AssignmentLateIcon fontSize='small' color='warning' /> */}
                    <svg width="24" height="20" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="15" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill="#333">HW</text>
                        <line x1="0" y1="10" x2="18" y2="10" stroke="#ff0000" strokeWidth="2"/>
                    </svg>
                </Tooltip>
            )}
            {!event.is_canceled && !event.class_note_exists && deadline && NewYork_Now.toJSDate() > deadline.toJSDate() && eventEndTime.toJSDate() < deadline.toJSDate() && (
                <Tooltip title="Class note does not exist" placement="top" arrow>
                    {/* <SpeakerNotesOffIcon fontSize='small' color='warning' /> */}
                    <svg width="24" height="20" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="15" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill="#333">CN</text>
                        <line x1="0" y1="10" x2="18" y2="10" stroke="#ff0000" strokeWidth="2"/>
                    </svg>
                </Tooltip>
            )}
            {event.course && (event.course.private_total_classes === null || event.course.private_total_classes === 0) && (
                <Tooltip title="Total number of class sessions are not set" placement="top" arrow>
                    <PriorityHighIcon fontSize='medium' color='warning' />
                </Tooltip>
            )}
            {event.course && event.course.course_memo_2 && event.course.course_memo_2 !== "" && (
                <LightTooltip 
                    title={
                        event.course.course_memo_2.split("\n").map((item, key) => {
                            return <span key={key}>{item}<br/></span>
                        })
                    } 
                    placement="right-start"
                    arrow
                >
                    <ModeCommentIcon fontSize='small' color='info' />
                </LightTooltip>
            )}
        </Box>
        {showModuleCheckBox &&
            <Box
                sx={{
                    position: "absolute",
                    left: 2,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Checkbox
                    id={`module-checkbox-${event.id}`}
                    onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                            setCheckedModuleList((prev) => {
                                const obj = {
                                    admin_uuid: userId,
                                    zoom_id: zoom_id,
                                    date: date,
                                    _time: _time,
                                    event_idx: event_idx,
                                    event: event,
                                }
                                
                                const newCheckedAttendanceList = [...prev, obj];
                                return newCheckedAttendanceList;
                            });
                        } else {
                            const newCheckedAttendanceList = checkedModuleList.filter((item) => {
                                return !(item.admin_uuid === userId && item.zoom_id === zoom_id && item.date === date && item._time === _time && item.event_idx === event_idx);
                            });
                            setCheckedModuleList(newCheckedAttendanceList);
                        }
                    }}
                    sx={{ 
                        width: '14px',
                        height: '14px',
                        padding: '6px',
                        '& .MuiSvgIcon-root': { 
                            fontSize: '1rem',
                        },
                    }}
                />
            </Box>
        }
        <Button
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: 800,
                fontSize: getFontSize(event.start_end_diff),
                color: colors.blueAccent[500],
                textAlign: "center",
                width: "100%",
                height: "100%",
                color: event.is_canceled_int && "red !important",
                padding: (!event.start_end_diff || event.start_end_diff === "1.00") ? "0px" : "6px 8px",
                textTransform: "none",
            }}
            disabled={isThisAdminInViewMode}
            onClick={handleButtonClick}
        >
            {event.title && (
                <Typography sx={{ fontSize: getFontSize(event.start_end_diff), marginTop: 1 }}>
                    {getEventTitle()}
                </Typography>
            )}
            <Typography sx={{ fontSize: getFontSize(event.start_end_diff) }}>
                {getCurrentEventNum()} / {getTotalSessionNum()}
            </Typography>
            <Typography sx={{ fontSize: getFontSize(event.start_end_diff) }}>
                {getCurrentHours()} / {getTotalHours()}
            </Typography>
        </Button>
        <Popover
            id={popoverId}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopOverClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.paper",
                    width: "200px",
                    boxShadow: 4,
                }}
            >
                <ModuleScheduleUpdateButton
                    handlePopOverClose={handlePopOverClose}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <SetMakeUpEventButton
                    handlePopOverClose={handlePopOverClose}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <ModuleCancelButton
                    handlePopOverClose={handlePopOverClose}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <ModuleRecordedButton
                    handlePopOverClose={handlePopOverClose}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <ModuleZoomAssignButton
                    handlePopOverClose={handlePopOverClose}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    socketConnected={socketConnected}
                    socket={socket}
                    zooms={zoomRooms}
                />
                <ModuleTotalNumButton
                    course={event.course}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <ModuleTotalHoursButton
                    course={event.course}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <ModuleCurrentHoursAdjustmentButton
                    course={event.course}
                    currentMinutes={event.current_minutes || 0}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <FindSubstituteButton
                    handlePopOverClose={handlePopOverClose}
                    event={event}
                    colors={colors}
                    enqueueSnackbar={enqueueSnackbar}
                />
            </Box>
        </Popover></>
    )
}
export default ModuleButtonMenu